import { functionsAsiaEast as functions } from "./initializer";
import { httpsCallable } from "firebase/functions";

export type Sound = {
  id: string;
  audioType: string;
  originalOrCover: string;
  audioTitle: string;
  audioShortDescription: string;
  audioFullDescription: string;
  audioDuration: number;
  audioTags: string;
  audioGenres: string;
  voiceType: string;
  vocalType: string;
  audioBpm: number;
  audioLyric: string;
  audioJpyPrice: number;
  audioFileName: string;
  audioFileType: string;
  audioSampleUrl: string;
  audioReleasedAt: string;
  artistId: string;
  artistName: string;
  artistImage: string;
  youtubeSafety: boolean;
  audioLastEditAt: string;
  isPrivate: boolean;
  createdAt: string;
  updatedAt: string;
};

export type SoundProps = {
  texts?: string[];
  tags?: string[];
  types?: string[];
  size?: number;
  cursorId?: string;
  orderByKey: keyof Sound;
  sort: "asc" | "desc";
};

export type SoundResult = {
  items: Sound[];
  hasMore: boolean;
  nextCursor: string | null;
  totalPage: number;
};

export type SoundVectorProps = {
  text: string;
  type: string;
  isLimitDuration?: boolean;
  size?: number;
};

export type SoundSearchProps = {
  texts: string[];
  type: string;
  isLimitDuration?: boolean;
  size?: number;
};

export type VectorResult = Sound & {
  l2: number;
};

export type SoundVectorResult = {
  items: VectorResult[];
};

export type SearchSoundResult = {
  items: Sound[];
};

const _getVectorSounds = httpsCallable<SoundVectorProps, SoundVectorResult>(
  functions,
  "getSoundsVector"
);
const _getSoundUrl = httpsCallable<
  { soundId: string; token?: string },
  { sounrUrl: string }
>(functions, "getSoundUrl");
const _sendSoundEventLog = httpsCallable<
  { eventName: string; soundId?: string; remarks?: string },
  string
>(functions, "sendSoundEventLog");
const _getSoundsByIds = httpsCallable<
  { ids: string[] },
  { items: Sound[]; deletedIds: string[] }
>(functions, "getSoundsByIds");
const _refreshRoomSoundMasterToken = httpsCallable<{ roomId: string }>(
  functions,
  "refreshRoomSoundMasterToken"
);
const _getSeachSounds = httpsCallable<SoundSearchProps, SearchSoundResult>(
  functions,
  "getSeachSounds"
);

export const getVectorSounds = async (props: SoundVectorProps) => {
  const res = await _getVectorSounds(props);
  return res.data;
};

export const getSearchSounds = async (props: SoundSearchProps) => {
  const res = await _getSeachSounds(props);
  return res.data;
};

// Cache for sound URLs (soundId -> { url: string, expiry: number })
const soundUrlCache = new Map<string, { url: string; expiry: number }>();
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

export const getSoundUrl = async (soundId: string, token?: string) => {
  // Check cache first
  const cached = soundUrlCache.get(soundId);
  if (cached && Date.now() < cached.expiry) {
    return cached.url;
  }

  try {
    const res = await _getSoundUrl({ soundId, token });
    const url = res.data.sounrUrl;

    // Cache the URL with expiry time
    soundUrlCache.set(soundId, {
      url,
      expiry: Date.now() + CACHE_DURATION,
    });

    return url;
  } catch (_) {
    return null;
  }
};

export const sendSoundEventLog = async (
  eventName: string,
  soundId?: string,
  remarks?: string
) => {
  // Log in development environment
  if (process.env.NODE_ENV === "development") {
    console.log("[sendSoundEventLog]", { eventName, soundId, remarks });
    return null;
  }

  try {
    return await _sendSoundEventLog({ eventName, soundId, remarks });
  } catch (err) {
    return null;
  }
};

export const getSoundsByIds = async (ids: string[]) => {
  const res = await _getSoundsByIds({ ids });
  return res.data;
};

export const refreshRoomSoundMasterToken = async (roomId: string) => {
  try {
    const res = await _refreshRoomSoundMasterToken({ roomId });
    if (res.data) {
      return true;
    }
  } catch (err) {
    console.error(err);
  }
  return false;
};
